import { useState, type ReactNode } from "react";
import type { AppMetricsResponse, GrowthDelta, GrowthMetric, Mover, OverviewResponse, VersionAdoption } from "@apphq/shared/types";
import { countryNl, int, pct } from "../lib/format";
import { adoptionText, moversEmpty, ratingView, rpiView, sharePercent, shareLabel, signedPct, signedTone, whaleCell, whalesAvailable } from "../lib/insights";
import { usePrefs } from "../lib/prefs";
import { RatingChart } from "./LazyCharts";
import { Card, EmptyState, KpiCard } from "./ui";

/** Signed, coloured change chip; `—` (muted) when there is no comparable base. */
export function SignedDelta({ ratio }: { ratio: number | null | undefined }) {
  const tone = signedTone(ratio);
  if (tone === "none") return <span className="muted">—</span>;
  return <span className={`delta delta-${tone}`}>{signedPct(ratio)}</span>;
}

/** KPI tile "Omzet per install"; falls back to a hint instead of a misleading €0. */
export function RpiKpi({ rpi }: { rpi: OverviewResponse["rpi"] | AppMetricsResponse["kpis"]["rpi"] }) {
  const { env } = usePrefs();
  const v = rpiView(rpi, env);
  return <KpiCard label="Omzet per install" value={v.value} hint={v.hint} muted={v.state !== "value"} />;
}

const GROWTH_ROWS: ReadonlyArray<{ key: GrowthMetric; label: string }> = [
  { key: "mrr", label: "MRR" },
  { key: "revenue", label: "Omzet" },
  { key: "new_paid", label: "Nieuwe betalers" },
  { key: "dau", label: "Gebruikersdagen (DAU)" },
  { key: "active_on_phone", label: "Op telefoon" },
];

/** WoW / MoM per metric; independent of the period selector. */
export function GrowthCard({ deltas }: { deltas: Record<GrowthMetric, GrowthDelta> }) {
  const { env } = usePrefs();
  return (
    <Card title="Groei" aside={<span className="muted small">week en maand t.o.v. ervoor</span>}>
      <div className="growth" role="table" aria-label="Groei per metric">
        <div className="growth-row growth-head" role="row">
          <span role="columnheader">Metric</span>
          <span role="columnheader" className="num">Week</span>
          <span role="columnheader" className="num">Maand</span>
        </div>
        {GROWTH_ROWS.map((r) => {
          const d = deltas[r.key] ?? { wow: null, mom: null };
          return (
            <div className="growth-row" role="row" key={r.key}>
              <span role="cell">{r.label}</span>
              <span role="cell" className="num"><SignedDelta ratio={d.wow} /></span>
              <span role="cell" className="num"><SignedDelta ratio={d.mom} /></span>
            </div>
          );
        })}
      </div>
      <p className="muted small tight">
        {env === "sandbox" ? "Sandbox: groei wordt alleen voor productie berekend. " : ""}
        Een streepje betekent: nog geen vergelijkbare basis.
      </p>
    </Card>
  );
}

function MoverLine({ label, mover }: { label: string; mover: Mover }) {
  if (!mover) return null;
  return (
    <li>
      <span className="muted">{label}</span>
      <strong>{mover.name}</strong>
      <SignedDelta ratio={mover.delta} />
    </li>
  );
}

/** Revenue and usage share per app, plus biggest riser / faller (last 7 closed days vs the 7 before). */
export function ContributionCard({ contribution, movers, collapsible = false }: { contribution: OverviewResponse["contribution"]; movers: OverviewResponse["movers"]; collapsible?: boolean }) {
  const [open, setOpen] = useState(false);
  const top = contribution[0];
  if (collapsible) {
    return (
      <section className="card collapsible">
        <button type="button" className="collapse-head" aria-expanded={open} aria-controls="contribution-body" onClick={() => setOpen((o) => !o)}>
          <span className="collapse-title">
            <h2>Bijdrage per app</h2>
            {!open && top && top.revenue_share > 0 && <span className="muted small">{top.name} · {pct(top.revenue_share)} van de omzet</span>}
          </span>
          <span className="chevron" aria-hidden>›</span>
        </button>
        {open && (
          <div id="contribution-body" className="collapse-body">
            <ContributionBody contribution={contribution} movers={movers} />
          </div>
        )}
      </section>
    );
  }
  return (
    <Card title="Bijdrage per app" aside={<span className="muted small">omzetaandeel en gebruiksaandeel</span>}>
      <ContributionBody contribution={contribution} movers={movers} />
    </Card>
  );
}

function ContributionBody({ contribution, movers }: { contribution: OverviewResponse["contribution"]; movers: OverviewResponse["movers"] }) {
  return (
    <>
      {contribution.length === 0 ? (
        <EmptyState title="Nog geen bijdrage">Zodra er omzet of gebruik is, zie je hier welke app het meeste bijdraagt.</EmptyState>
      ) : (
        <ul className="contrib">
          {contribution.map((c) => (
            <li key={c.app_id}>
              <div className="bar-row">
                <span className="bar-label">{c.name}</span>
              </div>
              <ShareBar label="Omzet" share={c.revenue_share} />
              <ShareBar label="Gebruik" share={c.dau_share} />
            </li>
          ))}
        </ul>
      )}
      {!moversEmpty(movers) && (
        <>
          <h3 className="sub-head">Grootste stijger en daler</h3>
          <ul className="movers">
            <MoverLine label="Omzet ↑" mover={movers.revenue.up} />
            <MoverLine label="Omzet ↓" mover={movers.revenue.down} />
            <MoverLine label="Gebruik ↑" mover={movers.dau.up} />
            <MoverLine label="Gebruik ↓" mover={movers.dau.down} />
          </ul>
        </>
      )}
    </>
  );
}

function ShareBar({ label, share }: { label: string; share: number }) {
  return (
    <div className="share-line">
      <span className="muted small share-label">{label}</span>
      <div className="bar-track">
        <div className="bar-fill" style={{ width: `${sharePercent(share)}%` }} />
      </div>
      <span className="bar-value small">{shareLabel(share)}</span>
    </div>
  );
}

/** Top app / top country / whale shares. Whale shares stay hidden until there are enough paying customers. */
export function ConcentrationCard({ c }: { c: OverviewResponse["concentration"] }) {
  const w = c.whales;
  return (
    <Card title="Concentratie-risico" aside={<span className="muted small">hoe afhankelijk ben je van een paar bronnen</span>}>
      <div className="conc-grid">
        <ConcTile label="Grootste app" value={c.top_app ? pct(c.top_app.share) : "—"} hint={c.top_app?.name ?? "nog geen omzet"} />
        <ConcTile label="Grootste land" value={c.top_country ? pct(c.top_country.share) : "—"} hint={c.top_country ? countryNl(c.top_country.country) : "nog geen omzet"} />
      </div>
      <h3 className="sub-head">Grootste klanten (netto-opbrengst)</h3>
      {whalesAvailable(w) ? (
        <div className="conc-grid conc-grid-3">
          <ConcTile label="Top 1" value={whaleCell(w.top1)} />
          <ConcTile label="Top 5" value={whaleCell(w.top5)} />
          <ConcTile label="Top 10" value={whaleCell(w.top10)} />
        </div>
      ) : (
        <p className="muted tight">Te weinig betalende klanten ({int(w.paying_customers)} van minimaal 10) voor een betrouwbaar klantaandeel.</p>
      )}
      {w.unassigned > 0 && <p className="muted small tight">{int(w.unassigned)} betalingen zonder klant-ID zijn niet meegeteld.</p>}
    </Card>
  );
}

function ConcTile({ label, value, hint }: { label: string; value: string; hint?: ReactNode }) {
  return (
    <div className="device-tile">
      <div className="kpi-label">{label}</div>
      <div className="device-tile-value">{value}</div>
      {hint && <div className="muted small">{hint}</div>}
    </div>
  );
}

/** App Store rating: stars, count and trend line (NL storefront). */
export function RatingCard({ ratings }: { ratings: AppMetricsResponse["ratings"] }) {
  const v = ratingView(ratings.current_avg, ratings.current_count);
  return (
    <Card title="Beoordeling" aside={<span className="muted small">App Store, Nederland</span>}>
      {!v.has ? (
        <EmptyState title="Nog geen beoordelingen" icon="☆">
          Zodra de app beoordelingen heeft en de nachtelijke sync gelopen heeft, verschijnt hier het gemiddelde.
        </EmptyState>
      ) : (
        <>
          <div className="rating-hero">
            <span className="big-number">{v.avg}</span>
            <span className="rating-stars" aria-label={`${v.avg} van 5 sterren`}>{v.stars}</span>
            <span className="muted">{v.count} beoordelingen</span>
          </div>
          {ratings.series.length >= 2 ? (
            <RatingChart points={ratings.series} />
          ) : (
            <p className="muted tight">De trendlijn bouwt zich op met de nachtelijke sync; vanaf de tweede dag verschijnt de grafiek.</p>
          )}
        </>
      )}
    </Card>
  );
}

/** "X% op laatste versie" lines on top of the device splits. Nothing without version data. */
export function AdoptionLine({ appVersion, osVersion }: { appVersion: VersionAdoption | null | undefined; osVersion: VersionAdoption | null | undefined }) {
  const app = adoptionText(appVersion);
  const os = adoptionText(osVersion);
  if (!app && !os) return null;
  return (
    <div className="adoption">
      {app && (
        <div className="adoption-item">
          <span className="kpi-label">Appversie</span>
          <strong>{app}</strong>
        </div>
      )}
      {os && (
        <div className="adoption-item">
          <span className="kpi-label">iOS-versie</span>
          <strong>{os}</strong>
        </div>
      )}
    </div>
  );
}
